import type { PublicUser } from '@malerbetrieb/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError, NetworkError } from '../modules/api/client.js';
import { clearLocalData, db, META_KEYS, readMeta, writeMeta } from '../modules/offline/db.js';

/**
 * Anmeldezustand.
 *
 * Der zuletzt angemeldete Benutzer wird lokal gemerkt, damit die App nach
 * einem Neustart auf der Baustelle **ohne Netz** weiter bedienbar ist. Das
 * ist kein Ersatz für die Authentifizierung: Jeder Serverzugriff wird
 * weiterhin serverseitig anhand der Sitzung geprüft.
 */

export type AuthStatus =
  /** Noch nicht geprüft. */
  | 'unbekannt'
  /** Serverseitig bestätigt angemeldet. */
  | 'angemeldet'
  /** Lokal bekannt, Server aktuell nicht erreichbar. */
  | 'offline-angemeldet'
  /** Serverseitige Sitzung ist abgelaufen – erneute Anmeldung nötig. */
  | 'sitzung-abgelaufen'
  | 'abgemeldet';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null);
  const status = ref<AuthStatus>('unbekannt');
  const busy = ref(false);
  const fehler = ref<string | null>(null);

  const istAngemeldet = computed(() => user.value !== null && status.value !== 'abgemeldet');
  const istBuero = computed(() => user.value?.role === 'office');
  const darfSynchronisieren = computed(() => status.value === 'angemeldet');

  /** Beim Start: lokalen Stand laden, danach die Serversitzung prüfen. */
  async function starten(): Promise<void> {
    const gemerkt = await readMeta<PublicUser | null>('currentUser', null);
    if (gemerkt) {
      user.value = gemerkt;
      status.value = 'offline-angemeldet';
    }

    await sitzungPruefen();
  }

  async function sitzungPruefen(): Promise<void> {
    try {
      const response = await api.session();

      if (response.user) {
        await benutzerUebernehmen(response.user);
        status.value = 'angemeldet';
      } else if (user.value) {
        // Lokale Daten bleiben erhalten – nur die Serversitzung fehlt.
        status.value = 'sitzung-abgelaufen';
      } else {
        status.value = 'abgemeldet';
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        status.value = user.value ? 'offline-angemeldet' : 'abgemeldet';
        return;
      }
      status.value = user.value ? 'sitzung-abgelaufen' : 'abgemeldet';
    }
  }

  async function anmelden(username: string, password: string): Promise<boolean> {
    busy.value = true;
    fehler.value = null;

    try {
      const response = await api.login(username, password);
      await benutzerUebernehmen(response.user);
      status.value = 'angemeldet';
      return true;
    } catch (error) {
      if (error instanceof NetworkError) {
        fehler.value = 'Keine Verbindung zum Server. Für die erste Anmeldung wird Internet benötigt.';
      } else if (error instanceof ApiError) {
        fehler.value = error.message;
      } else {
        fehler.value = 'Anmeldung fehlgeschlagen.';
      }
      return false;
    } finally {
      busy.value = false;
    }
  }

  /**
   * Übernimmt den angemeldeten Benutzer. Wechselt die Person, werden die
   * lokalen Daten verworfen – aber nur, wenn nichts mehr aussteht.
   */
  async function benutzerUebernehmen(neu: PublicUser): Promise<void> {
    const bisher = await readMeta<string | null>(META_KEYS.currentUserId, null);

    if (bisher && bisher !== neu.id) {
      const offen = await db.outbox.count();
      if (offen === 0) await clearLocalData();
    }

    user.value = neu;
    await writeMeta('currentUser', neu);
    await writeMeta(META_KEYS.currentUserId, neu.id);
  }

  /**
   * Abmelden. Lokale Aufnahmen bleiben erhalten; sind noch Daten offen,
   * wird darauf hingewiesen, statt sie stillschweigend zu löschen.
   */
  async function abmelden(): Promise<void> {
    busy.value = true;
    try {
      await api.logout().catch(() => undefined);
    } finally {
      user.value = null;
      status.value = 'abgemeldet';
      await writeMeta('currentUser', null);
      busy.value = false;
    }
  }

  function sitzungAbgelaufenMelden(): void {
    if (user.value) status.value = 'sitzung-abgelaufen';
  }

  return {
    user,
    status,
    busy,
    fehler,
    istAngemeldet,
    istBuero,
    darfSynchronisieren,
    starten,
    sitzungPruefen,
    anmelden,
    abmelden,
    sitzungAbgelaufenMelden,
  };
});
